import React, { useState, useEffect, useCallback } from 'react';
import ReactPaginate from 'react-paginate';
import api from './services/api';
import { formattaData, formattaOra } from './utils/date';

/* Storico prenotazioni di un'azienda.
 *
 * Usato sia dall'admin (dentro la scheda di un'azienda) sia dall'esercente
 * sul proprio profilo: e' lo stesso endpoint, che decide da solo cosa
 * mostrare. Le colonne su dove e' finito l'incasso arrivano valorizzate solo
 * per l'admin, quindi qui si mostrano se presenti. */

const DIMENSIONE_PAGINA = 20;

const formattaImporto = (valore) =>
  valore === null || valore === undefined
    ? '—'
    : `${Number(valore).toFixed(2)} €`;

const etichettaStato = (stato) => {
  const mappa = {
    PAID: 'Pagata',
    PENDING: 'In attesa',
    FAILED: 'Fallita',
    REFUNDED: 'Rimborsata',
    CONFIRMED: 'Confermata',
    CANCELLED: 'Annullata',
    CHECKED_IN: 'Check-in',
  };
  return mappa[stato] || stato || '—';
};

const classeStato = (stato) => {
  if (stato === 'PAID' || stato === 'CONFIRMED' || stato === 'CHECKED_IN') return 'stato--ok';
  if (stato === 'FAILED' || stato === 'CANCELLED') return 'stato--ko';
  if (stato === 'REFUNDED') return 'stato--neutro';
  return 'stato--attesa';
};

/* Uno stato assente non e' uno stato: mostrarlo come badge colorato lo
 * farebbe sembrare un valore reale. Si rende come semplice trattino. */
const badgeStato = (stato) => {
  if (!stato) return <span className="storico__assente">—</span>;
  return <span className={`stato ${classeStato(stato)}`}>{etichettaStato(stato)}</span>;
};

const StoricoPrenotazioni = ({ idAzienda }) => {
  const [righe, setRighe] = useState([]);
  const [pagina, setPagina] = useState(0);
  const [pagineTotali, setPagineTotali] = useState(0);
  const [totale, setTotale] = useState(0);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState(null);

  const carica = useCallback(async () => {
    if (!idAzienda) return;
    setCaricamento(true);
    setErrore(null);
    try {
      const { data } = await api.storicoPrenotazioni(idAzienda, pagina, DIMENSIONE_PAGINA);
      setRighe(data.prenotazioni || []);
      setPagineTotali(data.pagineTotali || 0);
      setTotale(data.totale || 0);
    } catch (e) {
      // 403 ha un significato preciso: non e' un guasto, e' un permesso.
      setErrore(
        e.response?.status === 403
          ? 'Non hai i permessi per consultare questo storico.'
          : 'Impossibile caricare lo storico delle prenotazioni.'
      );
    } finally {
      setCaricamento(false);
    }
  }, [idAzienda, pagina]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (!idAzienda) {
    return <p className="storico__vuoto">Azienda non selezionata.</p>;
  }

  if (caricamento && righe.length === 0) {
    return <p className="storico__vuoto">Caricamento storico…</p>;
  }

  if (errore) {
    return <div className="avviso avviso--errore" role="alert">{errore}</div>;
  }

  if (righe.length === 0) {
    return (
      <p className="storico__vuoto">
        Nessuna prenotazione registrata per questa azienda.
      </p>
    );
  }

  // Presente solo nelle risposte destinate all'admin.
  const mostraIncasso = righe.some(
    (r) => r.incassoTrattenutoPiattaforma !== null && r.incassoTrattenutoPiattaforma !== undefined
  );

  return (
    <div className="storico">
      <p className="storico__conteggio">
        {totale} {totale === 1 ? 'prenotazione' : 'prenotazioni'}
      </p>

      <div className="tabella-scroll">
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Evento</th>
              <th>Cliente</th>
              <th>Ospiti</th>
              <th>Importo</th>
              <th>Pagamento</th>
              <th>Prenotazione</th>
              {mostraIncasso && <th>Incasso</th>}
            </tr>
          </thead>
          <tbody>
            {righe.map((r) => (
              <tr key={r.idPrenotazione}>
                <td>
                  {formattaData(r.dataPrenotazione)}
                  {r.oraPrenotazione ? ` · ${formattaOra(r.oraPrenotazione)}` : ''}
                </td>
                <td>{r.titoloEvento || '—'}</td>
                <td>
                  {r.nomeCliente || '—'}
                  {r.emailCliente && (
                    <span className="storico__email">{r.emailCliente}</span>
                  )}
                </td>
                <td>{r.numeroOspiti ?? '—'}</td>
                <td>{formattaImporto(r.importo)}</td>
                <td>{badgeStato(r.statoPagamento)}</td>
                <td>{badgeStato(r.statoPrenotazione)}</td>
                {mostraIncasso && (
                  <td>
                    {r.incassoTrattenutoPiattaforma ? (
                      <span className="stato stato--attesa" title="L'azienda non aveva l'onboarding Stripe completo">
                        Trattenuto
                      </span>
                    ) : (
                      <span className="stato stato--ok">
                        All'azienda
                        {r.commissioneCentesimi
                          ? ` (−${(r.commissioneCentesimi / 100).toFixed(2)} €)`
                          : ''}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagineTotali > 1 && (
        <ReactPaginate
          previousLabel={'Precedente'}
          nextLabel={'Successivo'}
          breakLabel={'…'}
          pageCount={pagineTotali}
          forcePage={pagina}
          marginPagesDisplayed={1}
          pageRangeDisplayed={3}
          onPageChange={(d) => setPagina(d.selected)}
          containerClassName={'pagination'}
          activeClassName={'active'}
        />
      )}
    </div>
  );
};

export default StoricoPrenotazioni;
